/**
 * CameraFovHelper — Phase 2-F. Compute framing-health metrics for the
 * Joint Attention dyad setup.
 *
 * Problem: webcam FoV is typically 60-78°. With two people seated at
 * normal distance one (or both) often partially falls outside the frame
 * or sits at an edge so a small head turn pushes them out. The detector
 * loses the face → joint-attention metrics break.
 *
 * This module is pure (no React) so it can be unit-tested. It returns
 * a verdict + per-face metrics that the React layer renders.
 */

export interface FBBox {
  x: number; y: number; w: number; h: number;  // normalised [0,1]
}

export type FramingVerdict =
  | 'no_face'
  | 'single_capacity_unknown'    // 1 face, can't tell if 2 will fit
  | 'single_room_for_two'         // 1 face, small enough that 2 should fit
  | 'single_too_close'             // 1 face filling > 35% width → back up
  | 'two_ready'                    // 2 faces, both with healthy margins
  | 'two_tight'                    // 2 faces, at least one near edge
  | 'two_clipped'                  // 2 faces, edge margin < 1 %
  ;

export interface FaceFramingInfo {
  /** Index in input array. */
  i: number;
  /** Min distance (normalised) from face bbox to any frame edge. */
  edgeMargin: number;
  /** Which edge is closest: 'left' | 'right' | 'top' | 'bottom'. */
  closestEdge: 'left' | 'right' | 'top' | 'bottom';
  /** Face bbox width / frame width — useful proxy for "this person is N
   *  meters from camera": larger = closer. */
  widthFrac: number;
}

export interface FramingResult {
  verdict: FramingVerdict;
  faces: FaceFramingInfo[];
  /** Short Korean message for the HUD / on-screen toast. */
  message: string;
  /** Aggregate health [0,1]: 0 = clipped / no face, 1 = both faces well
   *  inside the frame with comfortable margins. Useful for a meter. */
  health: number;
}

const HEALTHY_MARGIN = 0.10;    // 10 % of frame edge = comfortable
const TIGHT_MARGIN   = 0.03;    // < 3 % = warning
const CLIPPED_MARGIN = 0.01;    // ≤ 1 % = considered clipped

const MAX_SINGLE_WIDTH_FOR_TWO = 0.30;  // face filling > 30% → no room for 2
const MIN_SINGLE_WIDTH_FOR_TWO = 0.10;  // face < 10% width → plenty of room

export function analyzeFraming(bboxes: FBBox[]): FramingResult {
  if (bboxes.length === 0) {
    return { verdict: 'no_face', faces: [], message: '얼굴이 보이지 않음', health: 0 };
  }
  const faces: FaceFramingInfo[] = bboxes.map((b, i) => {
    const left = b.x;
    const right = 1 - (b.x + b.w);
    const top = b.y;
    const bottom = 1 - (b.y + b.h);
    let edgeMargin = left;
    let closestEdge: FaceFramingInfo['closestEdge'] = 'left';
    if (right < edgeMargin) { edgeMargin = right; closestEdge = 'right'; }
    if (top < edgeMargin) { edgeMargin = top; closestEdge = 'top'; }
    if (bottom < edgeMargin) { edgeMargin = bottom; closestEdge = 'bottom'; }
    return { i, edgeMargin, closestEdge, widthFrac: b.w };
  });

  if (bboxes.length === 1) {
    const f = faces[0];
    if (f.widthFrac > MAX_SINGLE_WIDTH_FOR_TWO) {
      return {
        verdict: 'single_too_close',
        faces, health: 0.25,
        message: `1인 화면 점유 ${(f.widthFrac*100).toFixed(0)}%. 2인을 위해 카메라에서 멀어지거나 광각으로 전환하세요.`,
      };
    }
    if (f.widthFrac < MIN_SINGLE_WIDTH_FOR_TWO) {
      return {
        verdict: 'single_room_for_two',
        faces, health: 0.6,
        message: '1인 인식 중. 현재 거리면 2인 들어올 공간 충분합니다.',
      };
    }
    return {
      verdict: 'single_capacity_unknown',
      faces, health: 0.4,
      message: '1인 인식 중. 2번째 사람이 옆에 앉으면 화각 확인 필요.',
    };
  }

  // 2+ faces
  const minMargin = Math.min(...faces.map(f => f.edgeMargin));
  const aggHealth = Math.min(1, Math.max(0, minMargin / HEALTHY_MARGIN));
  if (minMargin <= CLIPPED_MARGIN) {
    const worst = faces.find(f => f.edgeMargin === minMargin)!;
    return {
      verdict: 'two_clipped',
      faces, health: aggHealth,
      message: `2인 잡힘. face_${worst.i}가 ${worst.closestEdge} 가장자리에 잘릴 수 있음.`,
    };
  }
  if (minMargin < TIGHT_MARGIN) {
    const worst = faces.find(f => f.edgeMargin === minMargin)!;
    return {
      verdict: 'two_tight',
      faces, health: aggHealth,
      message: `2인 잡힘. face_${worst.i} ${worst.closestEdge} 여유 ${(minMargin*100).toFixed(1)}% — 머리 움직임 주의.`,
    };
  }
  return {
    verdict: 'two_ready',
    faces, health: aggHealth,
    message: `2인 ready. 최소 여백 ${(minMargin*100).toFixed(0)}%.`,
  };
}

/** UI color hint for the verdict (HUD / toast). */
export function verdictColor(v: FramingVerdict): string {
  switch (v) {
    case 'no_face':                  return '#6b7280';
    case 'single_too_close':         return '#ef4444';
    case 'single_capacity_unknown':  return '#eab308';
    case 'single_room_for_two':      return '#06b6d4';
    case 'two_clipped':              return '#ef4444';
    case 'two_tight':                return '#eab308';
    case 'two_ready':                return '#22c55e';
  }
}
